import Link from "next/link";
import type { Metadata } from "next";
import { FileText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export const metadata: Metadata = {
  title: "Terms of Service — RateRight",
  description:
    "Terms of Service for RateRight, the construction hiring marketplace. Australian consumer law compliant.",
};

export default function TermsPage() {
  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Link href="/">
            <Button variant="ghost" size="sm" className="mb-4 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to RateRight
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-muted-foreground">
            Last updated: 3 March 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-10 max-w-4xl">
        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. About These Terms</h2>
            <p>
              These Terms of Service (&quot;Terms&quot;) govern your use of the RateRight platform
              (&quot;Platform&quot;), operated by RateRight ABN [pending] (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
              By accessing or using the Platform, you agree to be bound by these Terms.
            </p>
            <p>
              RateRight is a construction workforce marketplace that connects contractors
              (&quot;Hirers&quot;) with construction workers (&quot;Workers&quot;) in Australia. We facilitate
              connections — we are not an employer, labour hire agency, or recruitment firm.
            </p>
            <p>
              These Terms are governed by the laws of New South Wales, Australia, and you
              agree to the non-exclusive jurisdiction of the courts of New South Wales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Eligibility</h2>
            <p>To use the Platform, you must:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Be at least 18 years of age</li>
              <li>Be legally permitted to work in Australia (for Workers)</li>
              <li>Hold any required licences, permits, or certifications for the construction work you perform or advertise</li>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Comply with all applicable Australian laws, including workplace health and safety legislation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. The Platform</h2>
            <h3 className="text-xl font-medium mb-2">3.1 What We Do</h3>
            <p>
              RateRight provides an AI-powered marketplace that matches Hirers with Workers
              based on skills, location, availability, and job requirements. Our services include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Worker profile creation (including voice-to-profile technology)</li>
              <li>AI-powered matching of Workers to job postings</li>
              <li>Communication tools between Hirers and Workers</li>
              <li>Ratings and review systems</li>
              <li>Payment processing for hiring fees</li>
            </ul>

            <h3 className="text-xl font-medium mb-2 mt-4">3.2 What We Don&apos;t Do</h3>
            <p>RateRight is a marketplace platform. We do not:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Employ, supervise, or control any Worker</li>
              <li>Guarantee the quality, safety, or legality of any work performed</li>
              <li>Verify the accuracy of all information provided by users</li>
              <li>Provide insurance, superannuation, or workers&apos; compensation</li>
              <li>Act as a labour hire agency under the <em>Labour Hire Licensing Act 2018</em> (where applicable)</li>
            </ul>
            <p className="mt-2">
              Hirers are solely responsible for ensuring compliance with all employment and
              workplace health and safety obligations, including under the <em>Work Health and
              Safety Act 2011</em> (Cth) and relevant state legislation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Accounts</h2>
            <p>
              You are responsible for maintaining the security of your account credentials.
              You must notify us immediately of any unauthorised access. We reserve the right
              to suspend or terminate accounts that violate these Terms or engage in fraudulent
              activity.
            </p>
            <p>
              You may not create multiple accounts, impersonate another person, or use another
              person&apos;s account without authorisation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Fees and Payment</h2>
            <p>
              RateRight charges a flat fee of <strong>$50 AUD per hire</strong> (&quot;Hiring Fee&quot;),
              payable by the Hirer upon confirming a hire through the Platform. This fee is:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Non-refundable once a hire is confirmed, except as required by Australian Consumer Law</li>
              <li>Inclusive of GST where applicable</li>
              <li>Processed securely via Stripe</li>
            </ul>
            <p className="mt-2">
              Workers are not charged any fee to use the Platform. RateRight does not handle
              wages, superannuation, or any payments between Hirers and Workers for work performed.
            </p>
            <p>
              Under the <em>Australian Consumer Law</em> (Schedule 2, Competition and Consumer
              Act 2010), you may be entitled to a refund if our services are not provided with
              due care and skill or are not fit for their stated purpose.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. User Conduct</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide false, misleading, or deceptive information</li>
              <li>Use the Platform for any unlawful purpose</li>
              <li>Harass, discriminate against, or threaten other users</li>
              <li>Attempt to circumvent the Platform to avoid paying fees</li>
              <li>Scrape, harvest, or collect data from the Platform without consent</li>
              <li>Interfere with the Platform&apos;s operation or security</li>
              <li>Post content that is offensive, defamatory, or infringes third-party rights</li>
            </ul>
            <p className="mt-2">
              Hirers must comply with all anti-discrimination legislation, including the
              <em> Age Discrimination Act 2004</em>, <em>Disability Discrimination Act 1992</em>,
              <em> Racial Discrimination Act 1975</em>, and <em>Sex Discrimination Act 1984</em> (Cth).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Ratings and Reviews</h2>
            <p>
              Users may leave ratings and reviews after a hire is completed. Reviews must be
              honest, fair, and based on genuine experience. We reserve the right to remove
              reviews that are defamatory, fraudulent, or violate these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Intellectual Property</h2>
            <p>
              All content, branding, software, and technology on the Platform is owned by
              RateRight or its licensors. You retain ownership of content you upload (such as
              profile information and photos) but grant us a non-exclusive, royalty-free licence
              to use, display, and process that content for the purpose of operating the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, RateRight&apos;s liability for any claim
              arising from your use of the Platform is limited to the amount of fees you have
              paid to us in the 12 months preceding the claim.
            </p>
            <p>
              Nothing in these Terms excludes or limits liability that cannot be excluded under
              Australian Consumer Law, including guarantees relating to the provision of services.
            </p>
            <p>
              We are not liable for any loss or damage arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The conduct of any Worker or Hirer</li>
              <li>Workplace injuries or incidents</li>
              <li>Disputes between Hirers and Workers regarding pay, conditions, or work quality</li>
              <li>Inaccurate information provided by users</li>
              <li>Service interruptions or technical failures beyond our reasonable control</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Dispute Resolution</h2>
            <p>
              If you have a dispute with another user, we encourage you to first attempt to
              resolve it directly. You may report issues via our{" "}
              <Link href="/report-issue" className="text-primary underline">
                issue reporting tool
              </Link>.
            </p>
            <p>
              If you have a dispute with RateRight, we will attempt to resolve it through
              good-faith negotiation. If unresolved, disputes may be referred to mediation
              under the rules of the Australian Disputes Centre before commencing legal proceedings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Termination</h2>
            <p>
              You may close your account at any time by contacting us. We may suspend or
              terminate your access if you breach these Terms, engage in fraudulent activity,
              or for any other reason at our discretion with reasonable notice.
            </p>
            <p>
              Upon termination, your right to use the Platform ceases immediately. Sections
              relating to liability, intellectual property, and dispute resolution survive
              termination.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material
              changes via email or through the Platform. Continued use of the Platform after
              changes take effect constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
            <p>
              If you have questions about these Terms, contact us at:
            </p>
            <ul className="list-none space-y-1 mt-2">
              <li><strong>Email:</strong> support@rateright.com.au</li>
              <li><strong>Platform:</strong> <Link href="/report-issue" className="text-primary underline">Report an Issue</Link></li>
            </ul>
          </section>
        </div>

        {/* Footer nav */}
        <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row gap-4 items-center justify-between">
          <Link href="/privacy">
            <Button variant="outline">Read our Privacy Policy</Button>
          </Link>
          <Link href="/">
            <Button variant="ghost">Back to RateRight</Button>
          </Link>
        </div>
      </div>
    </main>
  );
}
